import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  ArrowLeft, Save, Loader2, Trash2, Upload, GripVertical, Star,
  ImageIcon, FileText, DollarSign, Eye, Check, X, Plus,
  Sofa, BedDouble, Building2, Briefcase, Frame, Wand2, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROOM_ICONS = {
  living_room: Sofa,
  bedroom: BedDouble,
  hotel: Building2,
  office: Briefcase,
  framed: Frame,
};

const getImgSrc = (img) => {
  if (!img) return null;
  if (img.startsWith('/api/')) return `${BACKEND_URL}${img}`;
  if (img.startsWith('data:')) return img;
  return `data:image/jpeg;base64,${img}`;
};

export default function ProductEditorPage() {
  const { productId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Mockup studio
  const [roomTemplates, setRoomTemplates] = useState([]);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [generatingMockups, setGeneratingMockups] = useState(false);
  const [previewMockups, setPreviewMockups] = useState([]);

  // Editable fields
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [salesCopy, setSalesCopy] = useState('');

  // Images
  const [images, setImages] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);

  const loadProduct = useCallback(async () => {
    try {
      const res = await api.get(`/products/${productId}/edit`);
      const p = res.data;
      setProduct(p);
      setTitle(p.title || '');
      setSubtitle(p.subtitle || '');
      setDescription(p.description || '');
      setPrice(String(p.price || ''));
      setSalesCopy(p.sales_copy || '');
      setImages(p.images || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load product');
      navigate('/creator-dashboard');
    } finally {
      setLoading(false);
    }
  }, [productId, navigate]);

  useEffect(() => { loadProduct(); }, [loadProduct]);

  // Load room templates for visual products
  useEffect(() => {
    api.get('/products/room-templates').then(r => setRoomTemplates(r.data.templates)).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {};
      if (title !== product.title) updates.title = title;
      if (subtitle !== (product.subtitle || '')) updates.subtitle = subtitle;
      if (description !== product.description) updates.description = description;
      if (parseFloat(price) !== product.price) updates.price = parseFloat(price);
      if (salesCopy !== (product.sales_copy || '')) updates.sales_copy = salesCopy;

      if (Object.keys(updates).length > 0) {
        await api.put(`/products/${productId}/edit`, updates);
      }
      toast.success('Product saved');
      await loadProduct();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/products/${productId}/images/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Image added');
      await loadProduct();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const handleDeleteImage = async (idx) => {
    if (images.length <= 1) { toast.error('Product must have at least one image'); return; }
    try {
      await api.delete(`/products/${productId}/images/${idx}`);
      toast.success('Image removed');
      await loadProduct();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleSetCover = async (idx) => {
    if (idx === 0) return;
    try {
      const order = images.map((_, i) => i);
      await api.put(`/products/${productId}/images/reorder`, { order, cover_index: idx });
      toast.success('Cover image updated');
      await loadProduct();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reorder failed');
    }
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e) => e.preventDefault();

  const toggleRoom = (id) => {
    setSelectedRooms(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
    setPreviewMockups([]);
  };

  const handlePreviewMockups = async () => {
    if (!selectedRooms.length) { toast.error('Select at least one room template'); return; }
    setGeneratingMockups(true);
    try {
      const res = await api.post(`/products/${productId}/mockups/generate`, { room_types: selectedRooms, add_to_gallery: false });
      setPreviewMockups(res.data.generated || []);
      toast.success(`${res.data.count} mockup(s) generated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally { setGeneratingMockups(false); }
  };

  const handleAddMockupsToGallery = async () => {
    if (!selectedRooms.length) return;
    setGeneratingMockups(true);
    try {
      const res = await api.post(`/products/${productId}/mockups/generate`, { room_types: selectedRooms, add_to_gallery: true });
      toast.success(`${res.data.count} mockup(s) added to gallery`);
      setPreviewMockups([]);
      setSelectedRooms([]);
      await loadProduct();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add mockups');
    } finally { setGeneratingMockups(false); }
  };
  const handleDrop = async (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    const newOrder = images.map((_, i) => i);
    const [moved] = newOrder.splice(dragIdx, 1);
    newOrder.splice(targetIdx, 0, moved);
    try {
      await api.put(`/products/${productId}/images/reorder`, { order: newOrder, cover_index: 0 });
      toast.success('Gallery reordered');
      await loadProduct();
    } catch (err) {
      toast.error('Reorder failed');
    }
    setDragIdx(null);
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin text-amber-500" size={24} /></div>;
  if (!product) return null;

  const hasChanges = title !== product.title || subtitle !== (product.subtitle || '') ||
    description !== product.description || parseFloat(price) !== product.price ||
    salesCopy !== (product.sales_copy || '');

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-8" data-testid="product-editor-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link to="/creator-dashboard" className="text-muted-foreground hover:text-obsidian transition-colors">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="font-serif text-xl text-obsidian">Edit Product</h1>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{product.category || product.product_type}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link to={`/products/${productId}`}>
            <Button variant="outline" size="sm" className="text-xs" data-testid="view-live-btn">
              <Eye size={14} className="mr-1.5" /> View Live
            </Button>
          </Link>
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            size="sm"
            className="bg-obsidian hover:bg-amber-600 text-white text-xs"
            data-testid="save-product-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Save size={14} className="mr-1.5" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: Main editing area */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title & Subtitle */}
          <div className="bg-white border border-border/30 rounded-xl p-5 space-y-4" data-testid="editor-details">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Product Title</Label>
              <Input
                data-testid="editor-title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="mt-1 text-lg font-medium"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Subtitle</Label>
              <Input
                data-testid="editor-subtitle"
                value={subtitle}
                onChange={e => setSubtitle(e.target.value)}
                className="mt-1"
                placeholder="Optional subtitle"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Description</Label>
              <Textarea
                data-testid="editor-description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="mt-1 min-h-[140px] resize-none"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Sales Copy</Label>
              <Input
                data-testid="editor-sales-copy"
                value={salesCopy}
                onChange={e => setSalesCopy(e.target.value)}
                className="mt-1"
                placeholder="Short punchy sales line"
              />
            </div>
          </div>

          {/* Gallery Images */}
          <div className="bg-white border border-border/30 rounded-xl p-5" data-testid="editor-gallery">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-medium text-obsidian">Preview Images</h3>
                <p className="text-[10px] text-muted-foreground">Drag to reorder. First image is the marketplace cover.</p>
              </div>
              <label className="cursor-pointer">
                <Button size="sm" variant="outline" className="text-xs" disabled={uploading} asChild>
                  <span>
                    {uploading ? <Loader2 size={12} className="animate-spin mr-1" /> : <Plus size={12} className="mr-1" />}
                    Add Image
                  </span>
                </Button>
                <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
              </label>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {images.map((img, idx) => (
                <div
                  key={idx}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(idx)}
                  className={`relative group rounded-xl overflow-hidden border-2 transition-all cursor-grab active:cursor-grabbing ${
                    idx === 0 ? 'border-amber-400 ring-2 ring-amber-200' : 'border-border/30 hover:border-amber-200'
                  } ${dragIdx === idx ? 'opacity-50' : ''}`}
                  data-testid={`gallery-image-${idx}`}
                >
                  <div className="aspect-[4/3]">
                    <img src={getImgSrc(img)} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover" />
                  </div>
                  {/* Overlay controls */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <button
                      onClick={() => handleSetCover(idx)}
                      className={`p-1.5 rounded-full transition-colors ${idx === 0 ? 'bg-amber-500 text-white' : 'bg-white/90 text-obsidian hover:bg-amber-500 hover:text-white'}`}
                      title={idx === 0 ? 'Current cover' : 'Set as cover'}
                    >
                      <Star size={14} fill={idx === 0 ? 'currentColor' : 'none'} />
                    </button>
                    <button
                      onClick={() => handleDeleteImage(idx)}
                      className="p-1.5 rounded-full bg-white/90 text-red-500 hover:bg-red-500 hover:text-white transition-colors"
                      title="Remove image"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {/* Cover badge */}
                  {idx === 0 && (
                    <div className="absolute top-2 left-2 bg-amber-500 text-white text-[8px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full">
                      Cover
                    </div>
                  )}
                  {/* Drag handle */}
                  <div className="absolute top-2 right-2 bg-white/70 rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <GripVertical size={12} className="text-obsidian/60" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Room Mockup Studio — visual products only */}
          {(product.product_type === 'wallpaper' || product.product_type === 'framed_artwork' || product.category === 'wallpaper' || product.category === 'framed_artwork') && roomTemplates.length > 0 && (
            <div className="bg-white border border-border/30 rounded-xl p-5" data-testid="mockup-studio">
              <div className="flex items-center gap-2 mb-1">
                <Wand2 size={16} className="text-violet-500" />
                <h3 className="text-sm font-medium text-obsidian">Room Mockup Studio</h3>
              </div>
              <p className="text-[10px] text-muted-foreground mb-4">Generate styled interior previews to showcase your product in real settings.</p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                {roomTemplates.map(t => {
                  const Icon = ROOM_ICONS[t.id] || ImageIcon;
                  const selected = selectedRooms.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      onClick={() => toggleRoom(t.id)}
                      data-testid={`room-${t.id}`}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${
                        selected
                          ? 'border-violet-400 bg-violet-50 shadow-sm'
                          : 'border-border/30 hover:border-violet-200 bg-white'
                      }`}
                    >
                      <Icon size={18} className={selected ? 'text-violet-500' : 'text-stone-400'} />
                      <span className={`text-[10px] font-medium leading-tight ${selected ? 'text-violet-700' : 'text-muted-foreground'}`}>{t.label}</span>
                      {selected && <Check size={10} className="text-violet-500" />}
                    </button>
                  );
                })}
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handlePreviewMockups}
                  disabled={generatingMockups || !selectedRooms.length}
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs"
                  data-testid="preview-mockups-btn"
                >
                  {generatingMockups ? <Loader2 size={12} className="animate-spin mr-1" /> : <Eye size={12} className="mr-1" />}
                  Preview
                </Button>
                <Button
                  onClick={handleAddMockupsToGallery}
                  disabled={generatingMockups || !selectedRooms.length}
                  size="sm"
                  className="flex-1 text-xs bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="add-mockups-btn"
                >
                  {generatingMockups ? <Loader2 size={12} className="animate-spin mr-1" /> : <Plus size={12} className="mr-1" />}
                  Add to Gallery
                </Button>
              </div>

              {/* Mockup previews */}
              {previewMockups.length > 0 && (
                <div className="mt-4 space-y-2" data-testid="mockup-previews">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Preview</p>
                  <div className="grid grid-cols-2 gap-2">
                    {previewMockups.map((m, i) => (
                      <div key={i} className="rounded-lg overflow-hidden border border-violet-200 shadow-sm">
                        <img src={m.url} alt={m.label} className="w-full object-cover" />
                        <div className="bg-violet-50 px-2 py-1">
                          <p className="text-[9px] text-violet-700 font-medium">{m.label}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: Sidebar */}
        <div className="space-y-5">
          {/* Price */}
          <div className="bg-white border border-border/30 rounded-xl p-5" data-testid="editor-pricing">
            <h3 className="text-sm font-medium text-obsidian mb-3 flex items-center gap-1.5">
              <DollarSign size={14} className="text-amber-500" /> Pricing
            </h3>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Price (EUR)</Label>
              <Input
                data-testid="editor-price"
                type="number"
                min="0.50"
                step="0.01"
                value={price}
                onChange={e => setPrice(e.target.value)}
                className="mt-1 font-serif text-2xl h-14"
              />
            </div>
          </div>

          {/* Creator Protection */}
          <div className="bg-emerald-50/60 border border-emerald-200/50 rounded-xl p-4" data-testid="editor-protection-notice">
            <div className="flex gap-2.5">
              <ShieldCheck size={16} className="text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-emerald-800">Creator Protection Enabled</p>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                  <span className="text-[9px] text-emerald-700/80">Secure File Delivery</span>
                  <span className="text-[9px] text-emerald-700/80">Protected Downloads</span>
                  <span className="text-[9px] text-emerald-700/80">Full Creator Ownership</span>
                </div>
              </div>
            </div>
          </div>

          {/* Download file (locked) */}
          <div className="bg-stone-50 border border-border/30 rounded-xl p-5" data-testid="editor-download-file">
            <h3 className="text-sm font-medium text-obsidian mb-3 flex items-center gap-1.5">
              <FileText size={14} className="text-emerald-500" /> Download File
            </h3>
            <p className="text-[10px] text-muted-foreground mb-3">The original product file. This is what buyers download after purchase.</p>
            {product.download_files?.map((f, i) => (
              <div key={i} className="flex items-center gap-2 p-2.5 bg-white rounded-lg border border-border/20">
                <FileText size={14} className="text-amber-500 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-obsidian truncate">{f.name}</p>
                  <p className="text-[9px] text-muted-foreground">{f.mime_type}</p>
                </div>
                <div className="text-[8px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold uppercase shrink-0">
                  Locked
                </div>
              </div>
            ))}
          </div>

          {/* Specs */}
          {product.specs && (
            <div className="bg-white border border-border/30 rounded-xl p-5" data-testid="editor-specs">
              <h3 className="text-sm font-medium text-obsidian mb-3">Product Specs</h3>
              <div className="space-y-2 text-xs">
                {Object.entries(product.specs).filter(([k]) => !['print_ready'].includes(k)).map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span className="text-muted-foreground capitalize">{k.replace(/_/g, ' ')}</span>
                    <span className="font-medium text-obsidian text-right">{String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Status */}
          <div className="bg-white border border-border/30 rounded-xl p-5">
            <h3 className="text-sm font-medium text-obsidian mb-2">Status</h3>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-emerald-700 font-medium">Published & Live</span>
            </div>
            <p className="text-[9px] text-muted-foreground mt-1">
              Created {product.created_at ? new Date(product.created_at).toLocaleDateString() : 'Unknown'}
              {product.updated_at && ` · Updated ${new Date(product.updated_at).toLocaleDateString()}`}
            </p>
            <p className="text-[9px] text-muted-foreground">{product.purchases || 0} purchases</p>
          </div>
        </div>
      </div>
    </div>
  );
}
